import { Bar, Doughnut, HorizontalBar, Line } from 'react-chartjs-2';
import {
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Popover from '@material-ui/core/Popover';
import Pagination from '@material-ui/lab/Pagination';
import AccessTimeOutlinedIcon from '@material-ui/icons/AccessTimeOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import SchoolOutlinedIcon from '@material-ui/icons/SchoolOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EmojiEventsOutlinedIcon from '@material-ui/icons/EmojiEventsOutlined';
import DateFnsUtils from '@date-io/date-fns';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import ptBR from 'date-fns/locale/pt-BR';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ManagementDashboardV2Data from 'services/dataService/ManagementDashboardV2Data';
import NotificationData from 'services/dataService/NotificationData';
import ReportData, { AdminReportType } from 'services/dataService/ReportData';
import { useStyles } from './styles';

const colors = [
  '#0084c3',
  '#5fcbeb',
  '#003057',
  '#4d4d4f',
  '#72afd0',
  '#9fcde1',
];

const formatNumber = (value) =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(
    Number(value || 0)
  );

const formatDecimal = (value) =>
  new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(Number(value || 0));

const truncateChartLabel = (value, maxLength = 18) => {
  const label = String(value || '');
  return label.length > maxLength
    ? `${label.slice(0, maxLength - 3)}...`
    : label;
};

const chartOptions = {
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    xAxes: [{ gridLines: { display: false }, ticks: { beginAtZero: true } }],
    yAxes: [{ gridLines: { color: '#edf2f4' }, ticks: { beginAtZero: true } }],
  },
};

const CSV_EXPORT_BLOCK_KEY = 'AnalyticsAcademicDashboardV2CsvExport';
const CSV_EXPORT_REQUESTED_AT_KEY =
  'AnalyticsAcademicDashboardV2CsvRequestedAt';
const CSV_EXPORT_BLOCK_TIME = 6 * 60 * 1000;

const hasCsvExportInProgress = () => {
  const blockUntil = Number(localStorage.getItem(CSV_EXPORT_BLOCK_KEY));
  return blockUntil > Date.now();
};

function FilterSelect({ label, value, options, onChange, multiple = false }) {
  const getOptionValue = (option) =>
    option.value || option.id || option.name || '';

  if (multiple) {
    const selectedOptions = options.filter((option) =>
      value.includes(getOptionValue(option))
    );

    return (
      <Autocomplete
        multiple
        options={options}
        value={selectedOptions}
        getOptionLabel={(option) =>
          option.label || option.name || option.value || option.id || ''
        }
        getOptionSelected={(option, selected) =>
          getOptionValue(option) === getOptionValue(selected)
        }
        filterSelectedOptions
        onChange={(event, selected) =>
          onChange(selected.map((option) => getOptionValue(option)))
        }
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={getOptionValue(option)}
              label={
                option.label || option.name || option.value || option.id || ''
              }
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label={label} variant="outlined" />
        )}
      />
    );
  }

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={false}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        label={label}
        renderValue={(selected) =>
          multiple ? (
            <div>
              {selected.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  style={{ margin: 2 }}
                />
              ))}
            </div>
          ) : (
            selected || 'Todos'
          )
        }
      >
        {!multiple && <MenuItem value="">Todos</MenuItem>}
        {options.map((option) => (
          <MenuItem key={getOptionValue(option)} value={getOptionValue(option)}>
            {option.label || option.name || option.value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const toDateInput = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromDateInput = (value) => (value ? new Date(`${value}T00:00:00`) : null);

function PeriodFilter({ value, options, dateFrom, dateTo, onApply }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftPeriod, setDraftPeriod] = useState(value);
  const [draftDateFrom, setDraftDateFrom] = useState(dateFrom || '');
  const [draftDateTo, setDraftDateTo] = useState(dateTo || '');

  const openPicker = (event) => {
    setDraftPeriod(value);
    setDraftDateFrom(dateFrom || '');
    setDraftDateTo(dateTo || '');
    setAnchorEl(event.currentTarget);
  };

  const closePicker = () => setAnchorEl(null);
  const isOpen = Boolean(anchorEl);
  const selectedOption = options.find((option) => option.value === value);
  const selectedLabel =
    value === 'custom' && dateFrom && dateTo
      ? `${new Intl.DateTimeFormat('pt-BR').format(
          fromDateInput(dateFrom)
        )} - ${new Intl.DateTimeFormat('pt-BR').format(fromDateInput(dateTo))}`
      : selectedOption?.label || 'Total';

  return (
    <>
      <Button
        className={classes.periodButton}
        fullWidth
        onClick={openPicker}
        endIcon={<ExpandMoreIcon fontSize="small" />}
      >
        <span>{selectedLabel}</span>
      </Button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className={classes.periodPopover}>
          <div className={classes.periodPresets}>
            {options.map((option) => (
              <Button
                key={option.value}
                className={
                  draftPeriod === option.value
                    ? classes.periodPresetActive
                    : classes.periodPreset
                }
                onClick={() => {
                  setDraftPeriod(option.value);
                  setDraftDateFrom('');
                  setDraftDateTo('');
                }}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <div className={classes.periodCalendarArea}>
            <div className={classes.periodCalendarTitle}>
              Selecione o intervalo de datas
            </div>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
              <div className={classes.periodCalendars}>
                <KeyboardDatePicker
                  autoOk
                  disableToolbar
                  variant="inline"
                  format="dd/MM/yyyy"
                  label="Data inicial"
                  value={fromDateInput(draftDateFrom)}
                  onChange={(date) => {
                    setDraftPeriod('custom');
                    setDraftDateFrom(toDateInput(date));
                  }}
                />
                <KeyboardDatePicker
                  autoOk
                  disableToolbar
                  variant="inline"
                  format="dd/MM/yyyy"
                  label="Data final"
                  value={fromDateInput(draftDateTo)}
                  minDate={fromDateInput(draftDateFrom) || undefined}
                  onChange={(date) => {
                    setDraftPeriod('custom');
                    setDraftDateTo(toDateInput(date));
                  }}
                />
              </div>
            </MuiPickersUtilsProvider>
            <div className={classes.periodActions}>
              <Button onClick={closePicker}>Cancelar</Button>
              <Button
                color="primary"
                variant="contained"
                disabled={
                  draftPeriod === 'custom' && (!draftDateFrom || !draftDateTo)
                }
                onClick={() => {
                  onApply({
                    period: draftPeriod,
                    dateFrom: draftPeriod === 'custom' ? draftDateFrom : '',
                    dateTo: draftPeriod === 'custom' ? draftDateTo : '',
                  });
                  closePicker();
                }}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
}

function Panel({ title, subtitle, actions, children, className }) {
  const classes = useStyles();

  return (
    <div className={`${classes.panel} ${className || ''}`}>
      <div className={classes.panelHeader}>
        <h2 className={classes.panelTitle}>{title}</h2>
        {actions}
      </div>
      {subtitle && <p className={classes.panelSubtitle}>{subtitle}</p>}
      {children}
    </div>
  );
}

function Kpi({ label, value, hint, icon, tone = 'blue' }) {
  const classes = useStyles();
  const iconStyle =
    tone === 'green'
      ? { backgroundColor: '#e9f0f7', color: '#003057' }
      : { backgroundColor: '#e4f5fb', color: '#0084c3' };

  return (
    <div className={classes.kpi}>
      <div className={classes.kpiHeader}>
        <div className={classes.kpiLabel}>{label}</div>
        <div
          className={`${classes.kpiIcon} ${classes[`kpiIcon${tone}`]}`}
          style={iconStyle}
        >
          {icon}
        </div>
      </div>
      <div className={classes.kpiValue}>{value}</div>
      <div className={classes.kpiHint}>{hint}</div>
    </div>
  );
}

export default function ManagementDashboardV2({ teamLeader = false }) {
  const classes = useStyles();
  const [filters, setFilters] = useState({
    courses: [],
    statuses: [],
    teams: [],
    trails: [],
    customFields: {},
    period: 'all',
    dateFrom: '',
    dateTo: '',
    search: '',
    page: 1,
    limit: 20,
  });
  const [filterOptions, setFilterOptions] = useState({
    courses: [],
    statuses: [],
    teams: [],
    trails: [],
    customFields: [],
    periods: [],
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingCsv, setDownloadingCsv] = useState(hasCsvExportInProgress);
  const [customFieldSettings, setCustomFieldSettings] = useState({});
  const [customFieldsDialogOpen, setCustomFieldsDialogOpen] = useState(false);

  const loadFilters = async () => {
    try {
      const [response, savedConfigResponse] = await Promise.all([
        teamLeader
          ? ManagementDashboardV2Data.getTeamLeaderFilters()
          : ManagementDashboardV2Data.getFilters(),
        ReportData.getReportFields(AdminReportType.ACADEMIC_DASHBOARD_V2).catch(
          () => ({ data: {} })
        ),
      ]);
      const catalog = response.data?.customFieldCatalog || [];
      const savedFields = savedConfigResponse.data?.table_titles || [];
      const hasSavedConfig = savedFields.length > 0;
      const savedEnabledKeys = new Set(
        savedFields
          .filter((field) => field.selected || field.hidden === false)
          .map((field) => field.field)
      );
      const enabledKeys = new Set(
        hasSavedConfig
          ? [...savedEnabledKeys]
          : (response.data?.customFields || []).map((field) => field.key)
      );
      const enabledCustomFields = catalog.filter((field) =>
        enabledKeys.has(field.key)
      );
      setFilterOptions({
        ...(response.data || {}),
        customFields: enabledCustomFields,
      });
      setCustomFieldSettings(
        catalog.reduce(
          (settings, field) => ({
            ...settings,
            [field.key]: enabledKeys.has(field.key),
          }),
          {}
        )
      );
      loadOverview(filters, enabledCustomFields);
    } catch (error) {
      toast.error('Não foi possível carregar os filtros do dashboard.');
    }
  };

  const loadOverview = async (currentFilters, selectedCustomFields) => {
    setLoading(true);

    try {
      const customFields = selectedCustomFields || filterOptions.customFields;
      const response = await (teamLeader
        ? ManagementDashboardV2Data.getTeamLeaderOverview({
            ...getPeriodFilters(currentFilters),
            customFieldKeys: (customFields || []).map((field) => field.key),
          })
        : ManagementDashboardV2Data.getOverview({
        ...getPeriodFilters(currentFilters),
        customFieldKeys: (customFields || []).map((field) => field.key),
      }));
      setData(response.data || {});
    } catch (error) {
      toast.error('Não foi possível carregar o dashboard acadêmico.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    if (!downloadingCsv) return undefined;

    const blockUntil = Number(localStorage.getItem(CSV_EXPORT_BLOCK_KEY));
    const remainingTime = blockUntil - Date.now();
    const requestedAt = Number(
      localStorage.getItem(CSV_EXPORT_REQUESTED_AT_KEY)
    );

    if (remainingTime <= 0) {
      localStorage.removeItem(CSV_EXPORT_BLOCK_KEY);
      setDownloadingCsv(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      localStorage.removeItem(CSV_EXPORT_BLOCK_KEY);
      localStorage.removeItem(CSV_EXPORT_REQUESTED_AT_KEY);
      setDownloadingCsv(false);
    }, remainingTime);

    let notificationTimer;
    const checkExportNotification = async () => {
      try {
        const response = await NotificationData.getNotifications();
        const notifications = response.data?.notification || [];
        const reportReady = notifications.some(
          (notification) =>
            notification.title === 'Download Dashboard Acadêmico' &&
            new Date(notification.available_at).getTime() >= requestedAt
        );

        if (reportReady) {
          clearInterval(notificationTimer);
          clearTimeout(timer);
          localStorage.removeItem(CSV_EXPORT_BLOCK_KEY);
          localStorage.removeItem(CSV_EXPORT_REQUESTED_AT_KEY);
          setDownloadingCsv(false);
          toast.success(
            'O relatório do Dashboard Acadêmico está pronto. Acesse as notificações para baixar.'
          );
        }
      } catch (error) {
        // A falha na consulta de notificações não deve interromper a geração.
      }
    };

    notificationTimer = setInterval(checkExportNotification, 5000);
    checkExportNotification();

    return () => {
      clearTimeout(timer);
      clearInterval(notificationTimer);
    };
  }, [downloadingCsv]);

  const updateFilters = (nextFilters) => {
    const updated = { ...filters, ...nextFilters, page: 1 };
    setFilters(updated);
    loadOverview(updated);
  };

  const shiftMonth = (value, amount) => {
    const [year, month] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1 + amount, 1));
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
      2,
      '0'
    )}`;
  };

  const getPeriodFilters = (currentFilters) => {
    const requestFilters = { ...currentFilters };
    delete requestFilters.period;

    if (currentFilters.period === 'custom') {
      delete requestFilters.dateFrom;
      delete requestFilters.dateTo;
      return {
        ...requestFilters,
        ...(currentFilters.dateFrom && currentFilters.dateTo
          ? {
              dateFrom: currentFilters.dateFrom,
              dateTo: currentFilters.dateTo,
            }
          : {}),
      };
    }

    delete requestFilters.dateFrom;
    delete requestFilters.dateTo;

    if (!currentFilters.period || currentFilters.period === 'all') {
      return requestFilters;
    }

    const latestPeriod = filterOptions.periods?.[0]?.value;
    if (currentFilters.period.indexOf('last') === 0 && latestPeriod) {
      const months = Number(currentFilters.period.replace('last', ''));
      requestFilters.monthFrom = shiftMonth(latestPeriod, -(months - 1));
      requestFilters.monthTo = latestPeriod;
    } else {
      requestFilters.monthFrom = currentFilters.period;
      requestFilters.monthTo = currentFilters.period;
    }

    return requestFilters;
  };

  const statusChart = useMemo(
    () => ({
      labels: (data?.statuses || []).map((item) => item.name),
      datasets: [
        {
          data: (data?.statuses || []).map((item) => item.value),
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    }),
    [data]
  );

  const conclusionChart = useMemo(
    () => ({
      labels: (data?.conclusions || []).map((item) => item.month),
      datasets: [
        {
          data: (data?.conclusions || []).map((item) => item.value),
          borderColor: '#0084c3',
          backgroundColor: 'rgba(95, 203, 235, 0.18)',
          pointBackgroundColor: '#0084c3',
          fill: true,
          lineTension: 0.35,
        },
      ],
    }),
    [data]
  );

  const courseChart = useMemo(
    () => ({
      labels: (data?.courses || []).map((item) => item.name),
      datasets: [
        {
          data: (data?.courses || []).map((item) => item.completed),
          backgroundColor: '#003057',
          hoverBackgroundColor: '#0084c3',
        },
      ],
    }),
    [data]
  );

  const teamChart = useMemo(
    () => ({
      labels: (data?.teams || []).map((item) => item.name),
      datasets: [
        {
          data: (data?.teams || []).map((item) => item.value),
          backgroundColor: '#0084c3',
          hoverBackgroundColor: '#003057',
        },
      ],
    }),
    [data]
  );

  const customFieldChart = useMemo(
    () => ({
      labels: (data?.customFieldCompletions || []).map((item) => item.name),
      datasets: [
        {
          data: (data?.customFieldCompletions || []).map((item) => item.value),
          backgroundColor: '#5fcbeb',
          hoverBackgroundColor: '#0084c3',
        },
      ],
    }),
    [data]
  );

  const customFieldChartOptions = useMemo(
    () => ({
      ...chartOptions,
      scales: {
        ...chartOptions.scales,
        xAxes: [
          {
            ...chartOptions.scales.xAxes[0],
            ticks: {
              ...chartOptions.scales.xAxes[0].ticks,
              autoSkip: false,
              maxRotation: 45,
              minRotation: 45,
              callback: (value, index) =>
                truncateChartLabel(customFieldChart.labels[index]),
            },
          },
        ],
      },
      tooltips: {
        callbacks: {
          title: (tooltipItems) =>
            customFieldChart.labels[tooltipItems[0].index],
          label: (tooltipItem) =>
            `Conclusões: ${formatNumber(
              customFieldChart.datasets[0].data[tooltipItem.index]
            )}`,
        },
      },
    }),
    [customFieldChart]
  );

  const resetFilters = () => {
    const reset = {
      ...filters,
      courses: [],
      statuses: [],
      teams: [],
      trails: [],
      customFields: {},
      period: 'all',
      dateFrom: '',
      dateTo: '',
      search: '',
      page: 1,
    };
    setFilters(reset);
    loadOverview(reset);
  };

  const saveCustomFieldSettings = async () => {
    const catalog = filterOptions.customFieldCatalog || [];
    const updatedFilters = {
      ...filters,
      customFields: Object.fromEntries(
        Object.entries(filters.customFields).filter(
          ([key]) => customFieldSettings[key]
        )
      ),
      page: 1,
    };

    const tableTitles = catalog.map((field, index) => ({
      title: field.label,
      field: field.key,
      selected: Boolean(customFieldSettings[field.key]),
      hidden: !customFieldSettings[field.key],
      order: index,
    }));

    try {
      await ReportData.updateFields({
        type: AdminReportType.ACADEMIC_DASHBOARD_V2,
        table_titles: tableTitles,
      });
      setFilterOptions({
        ...filterOptions,
        customFields: catalog.filter((field) => customFieldSettings[field.key]),
      });
      setFilters(updatedFilters);
      setCustomFieldsDialogOpen(false);
      loadOverview(
        updatedFilters,
        catalog.filter((field) => customFieldSettings[field.key])
      );
    } catch (error) {
      toast.error('Não foi possível salvar os filtros personalizados.');
    }
  };

  const downloadCsv = async () => {
    if (downloadingCsv) return;

    setDownloadingCsv(true);
    localStorage.setItem(
      CSV_EXPORT_BLOCK_KEY,
      String(Date.now() + CSV_EXPORT_BLOCK_TIME)
    );
    localStorage.setItem(CSV_EXPORT_REQUESTED_AT_KEY, String(Date.now()));
    toast.info(
      'O relatório está sendo preparado e estará pronto em alguns instantes.'
    );

    try {
      await (teamLeader
        ? ManagementDashboardV2Data.requestTeamLeaderCsvExport({
            ...getPeriodFilters(filters),
            customFieldColumns: (filterOptions.customFields || []).map((field) => ({
              key: field.key,
              label: field.label,
            })),
          })
        : ManagementDashboardV2Data.requestCsvExport({
        ...getPeriodFilters(filters),
        customFieldColumns: (filterOptions.customFields || []).map((field) => ({
          key: field.key,
          label: field.label,
        })),
      }));
      toast.info(
        'O relatório será disponibilizado nas notificações quando estiver pronto.'
      );
    } catch (error) {
      localStorage.removeItem(CSV_EXPORT_BLOCK_KEY);
      localStorage.removeItem(CSV_EXPORT_REQUESTED_AT_KEY);
      setDownloadingCsv(false);
      toast.error('Não foi possível baixar o CSV.');
    }
  };

  const handlePageChange = (event, page) => {
    const updated = { ...filters, page };
    setFilters(updated);
    loadOverview(updated);
  };

  const formatDate = (value) => {
    if (!value) return 'n/a';
    return new Intl.DateTimeFormat('pt-BR').format(new Date(value));
  };

  const formatDateTime = (value) => {
    if (!value) return 'aguardando carga';
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value));
  };

  const formatCustomFieldValue = (value, key) => {
    if (value === null || value === undefined || value === '') return 'n/a';

    const isDateField = /(data|date|admiss|nasc|birth)/i.test(key || '');
    if (!isDateField) return String(value);

    let date;
    if (typeof value === 'number' && value > 20000 && value < 70000) {
      date = new Date(Date.UTC(1899, 11, 30 + value));
    } else {
      date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) return String(value);

    return new Intl.DateTimeFormat('pt-BR').format(date);
  };

  const indicators = data?.indicators || {};
  const customFieldColumns = filterOptions.customFields || [];
  const statusTotal = (data?.statuses || []).reduce(
    (total, item) => total + Number(item.value || 0),
    0
  );

  const periodOptions = [
    { value: 'all', label: 'Total' },
    { value: 'last3', label: 'Últimos 3 meses' },
    { value: 'last6', label: 'Últimos 6 meses' },
    { value: 'last12', label: 'Últimos 12 meses' },
    ...(filterOptions.periods || []).map((item) => ({
      value: item.value,
      label: new Intl.DateTimeFormat('pt-BR', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(`${item.value}-01T00:00:00Z`)),
    })),
  ];

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <div className={classes.eyebrow}>Analytics acadêmico</div>
          <h1 className={classes.title}>Dashboard Acadêmico</h1>
          <p className={classes.subtitle}>
            Uma visão clara da evolução dos alunos, cursos e resultados.
          </p>
        </div>
        <div className={classes.updated}>
          <span className={classes.liveDot} />
          Atualizado em {formatDateTime(indicators.updated_at)}
        </div>
      </div>

      <div className={classes.filterPanel}>
        <div className={classes.filterHeader}>
          <div className={classes.filterTitle}>Filtre a visão do dashboard</div>
          <Button
            className={classes.clearButton}
            onClick={resetFilters}
            startIcon={<RefreshOutlinedIcon />}
          >
            Limpar filtros
          </Button>
        </div>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.filterLabel}>Curso</span>
            <FilterSelect
              label="Todos os cursos"
              value={filters.courses}
              multiple
              options={filterOptions.courses || []}
              onChange={(value) => updateFilters({ courses: value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.filterLabel}>Status</span>
            <FilterSelect
              label="Todos os status"
              value={filters.statuses}
              multiple
              options={filterOptions.statuses || []}
              onChange={(value) => updateFilters({ statuses: value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.filterLabel}>Time</span>
            <FilterSelect
              label="Todos os times"
              value={filters.teams}
              multiple
              options={filterOptions.teams || []}
              onChange={(value) => updateFilters({ teams: value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.filterLabel}>Trilha</span>
            <FilterSelect
              label="Todas as trilhas"
              value={filters.trails}
              multiple
              options={filterOptions.trails || []}
              onChange={(value) => updateFilters({ trails: value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <span className={classes.filterLabel}>Período de conclusão</span>
            <PeriodFilter
              value={filters.period}
              options={periodOptions}
              dateFrom={filters.dateFrom}
              dateTo={filters.dateTo}
              onApply={(period) => updateFilters(period)}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <span className={classes.filterLabel}>Busca rápida</span>
            <TextField
              className={classes.select}
              size="small"
              fullWidth
              variant="outlined"
              value={filters.search}
              placeholder="Aluno, e-mail ou curso"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Buscar"
                      size="small"
                      onClick={() => updateFilters({ search: filters.search })}
                    >
                      <SearchOutlinedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(event) =>
                setFilters({ ...filters, search: event.target.value })
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter')
                  updateFilters({ search: filters.search });
              }}
            />
          </Grid>
        </Grid>
        {(filterOptions.customFieldCatalog || []).length > 0 && (
          <>
            <div className={classes.customFieldsHeader}>
              <div className={classes.customFieldsDivider} />
              <Button
                className={classes.customFieldsButton}
                onClick={() => setCustomFieldsDialogOpen(true)}
              >
                + Mais filtros
              </Button>
            </div>
            <Grid container spacing={2}>
              {(filterOptions.customFields || []).map((field) => (
                <Grid item xs={12} sm={6} md={3} key={field.key}>
                  <span className={classes.filterLabel}>{field.label}</span>
                  <FilterSelect
                    label={`Todos - ${field.label}`}
                    value={filters.customFields[field.key] || []}
                    multiple
                    options={field.values || []}
                    onChange={(value) =>
                      updateFilters({
                        customFields: {
                          ...filters.customFields,
                          [field.key]: value,
                        },
                      })
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </div>

      <Dialog
        open={customFieldsDialogOpen}
        onClose={() => setCustomFieldsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Configurações do Dashboard</DialogTitle>
        <DialogContent>
          <div className={classes.dialogDescription}>
            Selecione os campos personalizados que ficarão disponíveis nos
            filtros do dashboard.
          </div>
          <div className={classes.customFieldsChecklist}>
            {(filterOptions.customFieldCatalog || []).map((field) => (
              <FormControlLabel
                key={field.key}
                control={
                  <Checkbox
                    color="primary"
                    checked={Boolean(customFieldSettings[field.key])}
                    onChange={(event) =>
                      setCustomFieldSettings({
                        ...customFieldSettings,
                        [field.key]: event.target.checked,
                      })
                    }
                  />
                }
                label={field.label}
              />
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomFieldsDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={saveCustomFieldSettings}
          >
            Salvar filtros
          </Button>
        </DialogActions>
      </Dialog>

      {loading && !data ? (
        <div className={classes.loading}>Carregando dados analíticos...</div>
      ) : (
        <>
          <Grid container spacing={2} style={{ marginBottom: 22 }}>
            {/* <Grid item xs={12} sm={6} md={3}>
              <Kpi
                label="Usuários ativos"
                value={formatNumber(indicators.active_users)}
                hint="alunos ativos na visão atual"
              />
            </Grid> */}
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                label="Matrículas"
                value={formatNumber(indicators.enrollments)}
                hint="matrículas acadêmicas"
                icon={<MenuBookOutlinedIcon fontSize="small" />}
                tone="blue"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                label="Treinamentos concluídos"
                value={formatNumber(indicators.completed)}
                hint="com data de conclusão"
                icon={<CheckCircleOutlineOutlinedIcon fontSize="small" />}
                tone="green"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                label="Aulas realizadas"
                value={formatNumber(indicators.lessons)}
                hint="aulas concluídas"
                icon={<SchoolOutlinedIcon fontSize="small" />}
                tone="blue"
              />
            </Grid>
            {/* <Grid item xs={12} sm={6} md={3}>
              <Kpi
                label="Horas de treinamento"
                value={formatDecimal(indicators.workload)}
                hint="carga horária acumulada"
                icon={<AccessTimeOutlinedIcon fontSize="small" />}
                tone="green"
              />
            </Grid> */}
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                label="Carga horária concluída"
                value={formatDecimal(indicators.completed_workload)}
                hint="horas de treinamentos finalizados"
                icon={<CheckCircleOutlineOutlinedIcon fontSize="small" />}
                tone="blue"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginBottom: 22 }}>
            <Grid item xs={12} md={4}>
              <Panel
                title="Status dos treinamentos"
                subtitle="Distribuição das matrículas filtradas"
              >
                <div className={classes.statusContent}>
                  {statusChart.labels.length ? (
                    <>
                      <div className={classes.statusChartBox}>
                        <Doughnut
                          data={statusChart}
                          options={{
                            maintainAspectRatio: false,
                            legend: { display: false },
                            cutoutPercentage: 62,
                          }}
                        />
                        <div className={classes.statusCenter}>
                          <strong>{formatNumber(statusTotal)}</strong>
                          <span>matrículas</span>
                        </div>
                      </div>
                      <div className={classes.statusLegend}>
                        {(data?.statuses || []).map((item, index) => {
                          const value = Number(item.value || 0);
                          const percentage = statusTotal
                            ? Math.round((value / statusTotal) * 100)
                            : 0;

                          return (
                            <div
                              className={classes.statusLegendRow}
                              key={item.name}
                            >
                              <span
                                className={classes.statusLegendDot}
                                style={{
                                  backgroundColor:
                                    colors[index % colors.length],
                                }}
                              />
                              <span className={classes.statusLegendName}>
                                {item.name}
                              </span>
                              <strong>{formatNumber(value)}</strong>
                              <span className={classes.statusLegendPercentage}>
                                {percentage}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className={classes.empty}>Sem dados para exibir</div>
                  )}
                </div>
              </Panel>
            </Grid>
            <Grid item xs={12} md={8}>
              <Panel
                title="Conclusões por mês"
                subtitle="Quantidade de treinamentos finalizados ao longo do tempo"
              >
                <div className={classes.chartBox}>
                  {conclusionChart.labels.length ? (
                    <Line data={conclusionChart} options={chartOptions} />
                  ) : (
                    <div className={classes.empty}>
                      Sem conclusões no período
                    </div>
                  )}
                </div>
              </Panel>
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginBottom: 22 }}>
            <Grid item xs={12}>
              <Panel
                title="Cursos com mais conclusões"
                subtitle="Os dez cursos com maior quantidade de treinamentos finalizados"
              >
                <div className={classes.wideChartBox}>
                  {courseChart.labels.length ? (
                    <HorizontalBar data={courseChart} options={chartOptions} />
                  ) : (
                    <div className={classes.empty}>Sem dados para exibir</div>
                  )}
                </div>
              </Panel>
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginBottom: 22 }}>
            <Grid item xs={12}>
              <Panel
                title={teamLeader ? 'Top concluintes' : 'Times com mais finalizações'}
                subtitle={
                  teamLeader
                    ? 'Colaboradores com mais treinamentos concluídos'
                    : 'Quantidade de treinamentos concluídos por time'
                }
              >
                {teamLeader ? (
                  data?.topConcluders?.length ? (
                    <div className={classes.podium}>
                      {[
                        data.topConcluders[1],
                        data.topConcluders[0],
                        data.topConcluders[2],
                      ]
                        .filter(Boolean)
                        .map((item) => {
                          const rank = data.topConcluders.indexOf(item) + 1;
                          return (
                            <div
                              className={
                                classes.podiumItem +
                                ' ' +
                                classes['podiumItem' + rank]
                              }
                              key={item.id_user}
                              title={item.name}
                            >
                              <div className={classes.podiumPosition}>
                                {rank === 1 && (
                                  <EmojiEventsOutlinedIcon
                                    className={classes.podiumTrophy}
                                  />
                                )}
                                {rank}º
                              </div>
                              <div className={classes.podiumName}>
                                {item.name}
                              </div>
                              <div className={classes.podiumCompleted}>
                                {formatNumber(item.completed)} conclusões
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className={classes.empty}>Sem dados para exibir</div>
                  )
                ) : (
                  <div className={classes.wideChartBox}>
                    {teamChart.labels.length ? (
                      <HorizontalBar data={teamChart} options={chartOptions} />
                    ) : (
                      <div className={classes.empty}>Sem dados para exibir</div>
                    )}
                  </div>
                )}
              </Panel>
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginBottom: 22 }}>
            <Grid item xs={12} md={6}>
              <Panel
                title="Conclusões por segmentos"
                subtitle="Treinamentos concluídos agrupados pelos segmentos de campos personalizados"
              >
                <div className={classes.chartBox}>
                  {customFieldChart.labels.length ? (
                    <Bar
                      data={customFieldChart}
                      options={customFieldChartOptions}
                    />
                  ) : (
                    <div className={classes.empty}>
                      Nenhum campo personalizado encontrado
                    </div>
                  )}
                </div>
              </Panel>
            </Grid>
            <Grid item xs={12} md={6}>
              <Panel title="Carga horária por colaborador">
                <div className={classes.workloadTableWrap}>
                  <table className={classes.workloadTable}>
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th>Matrículas</th>
                        <th>Carga horária</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.workloadByUser || []).map((item) => (
                        <tr key={item.id_user}>
                          <td>{item.name}</td>
                          <td>{formatNumber(item.enrollments)}</td>
                          <td>{formatDecimal(item.workload)} h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!data?.workloadByUser?.length && (
                    <div className={classes.empty}>Sem dados para exibir</div>
                  )}
                </div>
              </Panel>
            </Grid>
          </Grid>

          <Panel
            title="Colaboradores"
            subtitle="Dados consolidados do relatório acadêmico"
            actions={
              <Button
                className={classes.downloadButton}
                startIcon={<GetAppOutlinedIcon />}
                onClick={downloadCsv}
                disabled={downloadingCsv}
              >
                {downloadingCsv ? (
                  <>
                    <CircularProgress size={16} />
                    Preparando relatório...
                  </>
                ) : (
                  'Baixar CSV'
                )}
              </Button>
            }
          >
            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>Aluno</th>
                    <th>CPF</th>
                    <th>E-mail</th>
                    <th>Último acesso</th>
                    <th>Curso</th>
                    <th>Categoria</th>
                    <th>Carga horária</th>
                    <th>Matrícula</th>
                    <th>Finalizado em</th>
                    <th>Progresso</th>
                    <th>Nota</th>
                    <th>Status</th>
                    <th>Usuário</th>
                    <th>Times</th>
                    <th>Trilhas</th>
                    {customFieldColumns.map((field) => (
                      <th key={field.key}>{field.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data?.rows || []).map((row) => (
                    <tr key={row.id_enrollment}>
                      <td>{row.student_name}</td>
                      <td>{row.cpf || 'n/a'}</td>
                      <td>{row.email || 'n/a'}</td>
                      <td>{formatDate(row.last_login_at)}</td>
                      <td>{row.course_name}</td>
                      <td>{row.category || 'n/a'}</td>
                      <td>{formatDecimal(row.workload)}</td>
                      <td>{formatDate(row.enroll_created_at)}</td>
                      <td>{formatDate(row.finished_at)}</td>
                      <td>{formatDecimal(row.progress)}%</td>
                      <td>
                        {row.grade === null ? 'n/a' : formatDecimal(row.grade)}
                      </td>
                      <td>
                        <span className={classes.status}>
                          {row.approval_status || 'Sem status'}
                        </span>
                      </td>
                      <td>
                        {Number(row.status_user) === 1 ? 'Ativo' : 'Bloqueado'}
                      </td>
                      <td>{row.teams || 'n/a'}</td>
                      <td>{row.trails || 'n/a'}</td>
                      {customFieldColumns.map((field) => (
                        <td key={field.key}>
                          {formatCustomFieldValue(
                            row.custom_fields && row.custom_fields[field.key],
                            field.key
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data?.rows?.length && (
                <div className={classes.empty}>
                  Nenhuma matrícula encontrada
                </div>
              )}
            </div>
            <div className={classes.pagination}>
              <Pagination
                color="primary"
                page={data?.pagination?.page || 1}
                count={Math.max(
                  Math.ceil(
                    (data?.pagination?.total || 0) /
                      (data?.pagination?.limit || 20)
                  ),
                  1
                )}
                onChange={handlePageChange}
              />
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
